import { FormEvent, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { reiseApi } from '../../api/reise';

/** Innlogging med engangskode: kunden ber om en kode på e-post, og skriver den inn. */
export default function Innlogging({ token, onInnlogget }: { token: string; onInnlogget: () => void }) {
  const { t } = useTranslation();
  const [sendt, setSendt] = useState(false);
  const [kode, setKode] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);

  async function utfor(handling: () => Promise<void>) {
    setOpptatt(true);
    setFeil(null);
    try {
      await handling();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    } finally {
      setOpptatt(false);
    }
  }

  const sendKode = () =>
    utfor(async () => {
      await reiseApi.sendKode(token);
      setSendt(true);
      setKode('');
    });

  const bekreft = (event: FormEvent) => {
    event.preventDefault();
    return utfor(async () => {
      await reiseApi.bekreft(token, kode.trim());
      onInnlogget();
    });
  };

  return (
    <div className="border-t border-slate-100 pt-6">
      <p className="mb-6 flex gap-3 text-sm text-slate-600">
        <ShieldCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-aksent-600" />
        <span>{t('reise.innlogging.forklaring')}</span>
      </p>

      {sendt && (
        <form onSubmit={bekreft} className="mb-6">
          <p className="mb-4 melding-ok text-sm" role="status">
            {t('reise.innlogging.sendt')}
          </p>
          <label htmlFor="engangskode" className="mb-1.5 block text-sm font-medium text-slate-700">
            {t('reise.innlogging.kode')}
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              id="engangskode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={kode}
              onChange={(e) => setKode(e.target.value.replace(/\D/g, ''))}
              className="felt w-44 text-center font-mono text-xl tracking-[0.35em]"
              autoFocus
            />
            <button type="submit" disabled={opptatt || kode.length !== 6} className="knapp-primaer">
              {t('reise.innlogging.bekreft')}
            </button>
          </div>
        </form>
      )}

      {feil && (
        <p role="alert" className="mb-4 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}

      <button type="button" onClick={sendKode} disabled={opptatt} className={sendt ? 'knapp-stille -ml-3' : 'knapp-primaer w-full'}>
        {sendt ? t('reise.innlogging.nyKode') : t('reise.innlogging.sendKode')}
      </button>
    </div>
  );
}
